import { Component, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { KPI_EXCLUDES_ON_TRACK_IN_COUNT } from '@catalogues';
import { ChartSize, KpiKeys, ToggleLabels } from '@models';
import { CommonFunctionsService } from '@services';
import { TrendChartData } from 'app/models/TrendChartData';
import { KpiChartFacadeService } from 'app/services/kpi-chart-facade/kpi-chart-facade.service';
import { EChartsOption } from 'echarts';
import { ECharts } from 'echarts/core';
import { Subject } from 'rxjs';

const LINE_CHART_OPTION: EChartsOption = {
  title: {
    text: '',
    show: false,
    textStyle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: 'rgba(27, 27, 29, 1)'
    }
  },
  grid: {
    left: '5%',
    right: '5%',
    top: '5%',
    bottom: '10%',
    containLabel: true
  },
  xAxis: {
    name: '',
    nameLocation: 'middle',
    nameGap: 30,
    show: true,
    nameTextStyle: {
      color: 'rgba(27, 27, 29, 1)',
      fontSize: 12,
      fontWeight: 'bold'
    },
    type: 'category',
    axisLine: {
      show: true
    },
    splitLine: {
      show: false
    },
    axisLabel: {
      color: 'rgba(27, 27, 29, 1)',
      fontSize: 12,
      fontWeight: 'normal'
    },
    axisTick: {
      show: false
    }
  },
  yAxis: {
    name: '',
    nameLocation: 'middle',
    nameGap: 15,
    type: 'value',
    show: true,
    nameTextStyle: {
      color: 'rgba(27, 27, 29, 1)',
      fontSize: 12,
      fontWeight: 'normal'
    },
    axisTick: {
      show: false
    },
    axisLine: {
      show: true
    },
    axisLabel: {
      overflow: 'break',
      width: 100,
      margin: 0,
      align: 'right',
      padding: [0, 10, 0, 0],
      color: 'rgba(27, 27, 29, 1)',
      formatter: '{value}%',
      fontSize: 12,
      fontWeight: 'bold'
    }
  },
  series: [
    {
      type: 'line',
      itemStyle: {
        color: '#005DAA'
      }
    },
    {
      type: 'line',
      itemStyle: {
        color: '#F35200'
      }
    }
  ]
};

const DEFAULT_SERIES = [
  {
    type: 'line',
    symbol: 'circle',
    itemStyle: {
      color: '#005DAA'
    }
  },
  {
    type: 'line',
    step: 'start',
    symbol: 'circle',
    showSymbol: false,
    itemStyle: {
      color: '#F35200'
    },
    lineStyle: {
      color: '#F35200'
    }
  }
];

@Component({
  selector: 'trend-chart',
  templateUrl: './trend-chart.component.html',
  styleUrl: './trend-chart.component.scss'
})
export class TrendChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() trendChartData!: TrendChartData;
  @Input() size!: ChartSize;
  @Input() kpiKey?: KpiKeys; // Optional KPI key for toggle state persistence

  // Services
  private kpiChartFacade = inject(KpiChartFacadeService);
  private commonFunctionsService = inject(CommonFunctionsService);

  // Lifecycle management
  private destroy$ = new Subject<void>();
  private destroyed = false;
  chartTitle = '';
  chartInstance!: ECharts;
  chartOption: EChartsOption | any = LINE_CHART_OPTION;
  defaultSeries = DEFAULT_SERIES;

  isPercentChart = true;
  boundaryGapNeeded = 0;
  initialIsPercentChart: boolean | undefined;
  toggleLabels: ToggleLabels = {
    left: 'Percent',
    right: 'Count'
  };

  ngOnInit(): void {
    this.initializeToggleState();
  }

  ngOnChanges() {
    this.initializeToggleState();
    this.buildChartOptions();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChartInit(chart: ECharts) {
    this.chartInstance = chart;
    this.commonFunctionsService.resizeChartWhenContainerIsReady(chart, {
      shouldStop: () => this.destroyed || !!chart.isDisposed?.()
    });
  }

  onToggleClick() {
    const isCount = !this.trendChartData.isCount;
    this.trendChartData.isCount = isCount;

    if (isCount) {
      this.trendChartData.isPercentChart = false;
      if (
        this.trendChartData.legend &&
        !this.trendChartData.legend.includes('On track') &&
        !this.excludesOnTrackFromLegendInCount(this.kpiKey)
      ) {
        this.trendChartData.legend.push('On track');
      } else if (
        this.trendChartData.legend &&
        this.trendChartData.legend.includes('On track') &&
        this.excludesOnTrackFromLegendInCount(this.kpiKey)
      ) {
        this.excludeValueFromLegend('On track');
      }
    } else {
      if (!this.trendChartData.targetValue) {
        this.excludeValueFromLegend('On track');
      } else if (
        this.trendChartData.legend &&
        !this.trendChartData.legend.includes('On track') &&
        this.trendChartData.targetValue
      ) {
        this.trendChartData.legend.push('On track');
      }
      this.trendChartData.isPercentChart =
        this.initialIsPercentChart !== undefined ? this.initialIsPercentChart : true;
    }

    // Save toggle state for persistence
    this.saveToggleState();

    this.buildChartOptions();
  }

  private excludesOnTrackFromLegendInCount(kpiKey: KpiKeys | undefined): boolean {
    if (!kpiKey) return false;
    return KPI_EXCLUDES_ON_TRACK_IN_COUNT.includes(kpiKey);
  }

  private excludeValueFromLegend(excludedItem: string) {
    this.trendChartData.legend = this.trendChartData.legend.filter((item) => item !== excludedItem);
  }

  /**
   * Initialize toggle state from saved state
   */
  private initializeToggleState(): void {
    if (!this.trendChartData?.hasToggle || !this.kpiKey) {
      return;
    }

    // Get saved toggle state
    const savedState = this.kpiChartFacade.getToggleState(
      this.kpiKey,
      this.trendChartData.identifier
    );
    if (savedState !== null) {
      // For trend charts, we use the chart identifier with suffix to distinguish states
      // "trendChart_count" means count mode, "trendChart_percent" means percent mode
      const expectedCountId = this.trendChartData.identifier + '_count';
      const shouldBeCount = savedState === expectedCountId;

      if (this.trendChartData.isCount !== shouldBeCount) {
        this.trendChartData.isCount = shouldBeCount;

        if (shouldBeCount) {
          this.trendChartData.isPercentChart = false;
          if (
            this.trendChartData.legend &&
            !this.trendChartData.legend.includes('On track') &&
            !this.excludesOnTrackFromLegendInCount(this.kpiKey)
          ) {
            this.trendChartData.legend.push('On track');
          } else if (
            this.trendChartData.legend &&
            this.trendChartData.legend.includes('On track') &&
            this.excludesOnTrackFromLegendInCount(this.kpiKey)
          ) {
            this.excludeValueFromLegend('On track');
          }
        } else {
          if (!this.trendChartData.targetValue) {
            this.excludeValueFromLegend('On track');
          } else if (
            this.trendChartData.legend &&
            !this.trendChartData.legend.includes('On track') &&
            this.trendChartData.targetValue
          ) {
            this.trendChartData.legend.push('On track');
          }
          this.trendChartData.isPercentChart =
            this.initialIsPercentChart !== undefined ? this.initialIsPercentChart : true;
        }
      }
    }
  }

  /**
   * Save current toggle state
   */
  private saveToggleState(): void {
    if (!this.trendChartData?.hasToggle || !this.kpiKey) {
      return;
    }

    // For trend charts, save chart identifier with suffix to distinguish states
    // Use "_count" suffix when in count mode, "_percent" when in percent mode
    const toggleState = this.trendChartData.isCount
      ? ((this.trendChartData.identifier + '_count') as any)
      : ((this.trendChartData.identifier + '_percent') as any);

    this.kpiChartFacade.setToggleState(this.kpiKey, this.trendChartData.identifier, toggleState);
  }

  formatTrend(trendValue: number | undefined) {
    if (trendValue === undefined) {
      return '-';
    }
    if (this.isPercentChart) {
      return parseFloat((trendValue * 100).toFixed(1));
    }
    return trendValue;
  }

  private buildChartOptions() {
    if (this.initialIsPercentChart === undefined) {
      this.initialIsPercentChart = this.trendChartData.isPercentChart;
    }
    let fontSize = 12;
    let fontWeight = 'normal';
    let nameFontWeight = 'normal';
    if (this.size === 'large') {
      fontSize = 12;
      fontWeight = 'normal';
      nameFontWeight = 'bold';
    } else if (this.size === 'medium') {
      nameFontWeight = 'bold';
    }
    if (this.chartOption && this.trendChartData.series) {
      const gridBottom =
        this.size === 'medium'
          ? this.trendChartData?.hasToggle
            ? 58
            : 50
          : this.size === 'large'
            ? 46
            : 24;
      const gridTop = this.size === 'medium' ? 12 : this.size === 'large' ? 10 : 8;
      this.isPercentChart = this.trendChartData.isPercentChart;
      this.chartOption = {
        ...this.chartOption,
        title: {
          ...this.chartOption['title'],
          text: this.chartTitle
        },
        grid: {
          ...this.chartOption['grid'],
          top: gridTop,
          bottom: gridBottom,
          containLabel: true
        },
        yAxis: {
          ...this.chartOption['yAxis'],
          name: '',
          nameGap: this.size === 'large' ? 60 : 15,
          nameTextStyle: {
            ...this.chartOption['yAxis'].nameTextStyle,
            fontSize: fontSize,
            fontWeight: nameFontWeight,
            fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
          },
          axisLabel: {
            ...this.chartOption['yAxis'].axisLabel,
            fontSize: fontSize,
            fontWeight: fontWeight,
            fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif',
            formatter: (value: number, index: number) => {
              return this.yAxisFormatter(index, value);
            }
          }
        },
        xAxis: {
          ...this.chartOption['xAxis'],
          name:
            this.size === 'large' || this.size === 'medium' ? this.trendChartData.xAxisName : '',
          nameGap: this.size === 'large' ? 40 : 30,
          nameTextStyle: {
            ...this.chartOption['yAxis'].nameTextStyle,
            fontSize: fontSize,
            fontWeight: nameFontWeight,
            fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
          },
          axisLabel: {
            ...this.chartOption['xAxis'].axisLabel,
            fontSize: fontSize,
            fontWeight: fontWeight,
            fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
          },
          data: this.trendChartData.xAxisLabels
        },
        legend: {
          show: false
        }
      };
      const allSeries: any[] = [];
      this.populateSeriesData(allSeries);
      this.chartOption.series = allSeries;

      if (this.boundaryGapNeeded === Infinity) {
        // division by zero case
        this.chartOption.yAxis.min = 0;
        this.chartOption.yAxis.max = this.calculateYAxisMax();
      } else if (this.boundaryGapNeeded > 0 && this.boundaryGapNeeded !== Infinity) {
        this.chartOption.yAxis.boundaryGap = [0, `${this.boundaryGapNeeded}%`];
      }

      if (this.chartInstance) {
        this.chartInstance.clear();
        this.chartInstance.setOption(this.chartOption, true);
      }
    }
  }

  private calculateYAxisMax() {
    return this.isPercentChart
      ? 100
      : this.trendChartData.targetValue !== undefined
        ? this.trendChartData.targetValue * 2
        : undefined;
  }

  private populateSeriesData(allSeries: any[]) {
    if (
      !this.trendChartData ||
      !this.trendChartData.series ||
      this.trendChartData.series.length === 0
    ) {
      return;
    }
    const seriesStart = this.trendChartData.isCount ? 1 : 0;
    // Numerator and denominator series are always included for trend charts
    const seriesEnd = this.trendChartData.isCount
      ? this.excludesOnTrackFromLegendInCount(this.kpiKey)
        ? 2
        : 3
      : 1;
    for (let i = seriesStart; i < seriesEnd; i++) {
      const defaultSeriesIndex = seriesStart === 1 ? i - 1 : 0;
      const targetValue = this.trendChartData.targetValue;
      const overTarget = this.trendChartData.overTarget;
      const series: any = {
        ...this.defaultSeries[defaultSeriesIndex],
        name: this.trendChartData.series[i].name,
        data: this.trendChartData.series[i].data,
        symbolSize: this.size === 'large' ? 8 : this.size === 'medium' ? 6 : 4,
        markLine: this.trendChartData.series[i].hasMarkLine
          ? {
              animation: true,
              symbol: 'none',
              label: { show: false },
              data: [
                {
                  name: 'On track',
                  yAxis: this.formatTrend(this.trendChartData.targetValue),
                  lineStyle: {
                    color: '#F35200',
                    type: 'dashed'
                  }
                }
              ]
            }
          : undefined,
        markArea:
          this.trendChartData.series[i].hasMarkLine && this.trendChartData.targetValue !== undefined
            ? {
                silent: true,
                z: -1,
                itemStyle: {
                  color: 'rgba(173, 235, 173, 0.3)',
                  borderColor: 'transparent'
                },
                data: []
              }
            : undefined
      };
      if (series.markArea !== undefined) {
        const limit = Math.max(...series.data);
        const markAreaData = this.generateMarkAreaData(targetValue, overTarget, limit);
        series.markArea.data = [markAreaData];
      }
      allSeries.push(series);
    }
  }

  private generateMarkAreaData(
    targetValue: number | undefined,
    overTarget: boolean | undefined,
    limit: number
  ) {
    if (targetValue === undefined || overTarget === undefined) return [];
    const targetFormatted = Number(this.formatTrend(targetValue));
    const BUFFER_PERCENTAGE = 0.7;
    let yAxisLimit = limit * (1 + BUFFER_PERCENTAGE);
    if (overTarget) {
      if (targetFormatted > limit) {
        const gapNeeded = targetFormatted - limit;
        yAxisLimit = targetFormatted * (1 + BUFFER_PERCENTAGE);
        this.boundaryGapNeeded = Math.ceil((gapNeeded / limit) * 100) + limit;
      }
      return [
        {
          yAxis: targetFormatted
        },
        {
          yAxis: yAxisLimit
        }
      ];
    } else {
      /**
       * Check for edge case, when target direction is below the target line and
       * max value on series is way lower than target, in that case we need to
       * set the boundaryGap to be at least x times bigger in in relation
       * to the max value available on the series
       * this to make sure the target limit is visible
       */
      const targetRatio = targetFormatted / limit;
      if (targetRatio > 1.1) {
        this.boundaryGapNeeded = Math.ceil(targetRatio * 100);
      }
      return [
        {
          yAxis: 0
        },
        {
          yAxis: targetFormatted
        }
      ];
    }
  }

  private yAxisFormatter(index: number, value: number) {
    if (this.size === 'small') {
      return index % 2 === 0
        ? this.isPercentChart && !this.trendChartData?.isCount
          ? value + '%'
          : value
        : '';
    }
    return this.isPercentChart && !this.trendChartData?.isCount ? value + '%' : value;
  }

  getTrendContainerClasses(): Record<string, boolean> {
    const hasToggle = !!this.trendChartData?.hasToggle;

    return {
      small: this.size === 'small',
      medium: this.size === 'medium',
      large: this.size === 'large',
      'with-toggle': this.size === 'medium' && hasToggle,
      'without-toggle': this.size === 'medium' && !hasToggle
    };
  }

  getChartHeight(): string | null {
    if (this.size === 'small') {
      return '119px';
    }

    // Medium and large inherit height from the parent chart container chain.
    return null;
  }

  getNoDataHeight(): string | null {
    if (this.size === 'small') {
      return this.getChartHeight();
    }

    if (this.size === 'medium' || this.size === 'large') {
      return this.trendChartData?.hasToggle ? 'calc(100% - 9px)' : '100%';
    }

    return null;
  }
}
